import { tables, supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";

// Export the Message type
export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_type: 'staff' | 'parent';
  content: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  metadata?: {
    document_reference?: string;
    document_name?: string;
  }
}

export interface MessageWithReadStatus extends Message {
  is_read: boolean;
  read_at: string | null;
}

// Base conversation interface
export interface Conversation {
  id: string;
  family_id: string;
  organization_id: string;
  title: string;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  family?: {
    id: string;
    name: string;
  };
  lastMessage?: Message;
  unreadCount?: number;
}

export interface ConversationWithDetails extends Conversation {
  last_message: Message | null;
  unread_count: number;
}

// Export type alias for backward compatibility
export type ConversationWithFamily = ConversationWithDetails;

/**
 * Mark a message as read by a user
 */
export const markMessageAsRead = async (
  messageId: string,
  userId: string
): Promise<boolean> => {
  const context: ErrorContext = {
    operation: "markMessageAsRead",
    additionalInfo: { messageId, userId },
  };

  try {
    // Check if this message has already been read by this user
    const { data: existingRead, error: readCheckError } = await supabase
      .from('message_reads')
      .select()
      .eq("message_id", messageId)
      .eq("user_id", userId)
      .maybeSingle();

    if (readCheckError) {
      console.error("Error checking read status:", readCheckError);
      throw readCheckError;
    }

    // If no existing read, create one
    if (!existingRead) {
      const { error } = await supabase
        .from('message_reads')
        .insert({
          message_id: messageId,
          user_id: userId,
        });

      if (error) {
        console.error("Error marking as read:", error);
        throw error;
      }
    }

    return true;
  } catch (error) {
    handleError(
      error,
      "Failed to mark message as read",
      ErrorSeverity.WARNING,
      context
    );
    return false;
  }
};

export const getConversations = async (): Promise<Conversation[]> => {
  const context: ErrorContext = {
    operation: "getConversations",
  };

  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        family:family_id (
          id,
          name
        )
      `)
      .order("last_message_at", { ascending: false });

    if (error) throw error;
    return (data || []) as unknown as Conversation[];
  } catch (error) {
    handleError(
      error,
      "Failed to load conversations",
      ErrorSeverity.ERROR,
      context
    );
    return [];
  }
};

export const getMessages = async (conversationId: string, currentUserId?: string): Promise<MessageWithReadStatus[]> => {
  const context: ErrorContext = {
    operation: "getMessages",
    additionalInfo: { conversationId },
  };

  try {
    // First get the organization_id for this conversation
    const { data: conversationData, error: convError } = await supabase
      .from('conversations')
      .select('organization_id')
      .eq('id', conversationId)
      .single();
      
    if (convError) throw convError;
    
    // Get messages with related message_reads
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        message_reads (
          user_id,
          read_at
        )
      `)
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) throw error;

    // Process each message to add is_read flag
    const messagesWithReadStatus = data.map(message => {
      // Set is_read true if the current user has a message_reads entry for this message
      const isRead = message.message_reads && message.message_reads.some(r => r.user_id === currentUserId);
      const readAt = isRead
        ? (message.message_reads.find(r => r.user_id === currentUserId)?.read_at || null)
        : null;
      return {
        ...message,
        is_read: isRead,
        read_at: readAt
      };
    });
    
    return messagesWithReadStatus as unknown as MessageWithReadStatus[];
  } catch (error) {
    handleError(
      error,
      "Failed to load messages",
      ErrorSeverity.ERROR,
      context
    );
    return [];
  }
};

export const subscribeToConversation = (conversationId: string, callback: (message: Message) => void) => {
  const channel = supabase
    .channel(`messages-${conversationId}`)
    .on('postgres_changes', 
      { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
      (payload) => {
        if (payload.new) {
          callback(payload.new as unknown as Message);
        }
      }
    )
    .subscribe();

  return channel;
};

/**
 * Get messages for a specific conversation
 */
export const getMessagesForConversation = async (
  conversationId: string,
  currentUserId?: string
): Promise<MessageWithReadStatus[]> => {
  const context: ErrorContext = {
    operation: "getMessagesForConversation",
    additionalInfo: { conversationId },
  };

  try {
    // Get the organization_id for this conversation first
    const { data: conversationData, error: convError } = await supabase
      .from('conversations')
      .select('organization_id')
      .eq('id', conversationId)
      .single();
      
    if (convError) throw convError;
    
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        message_reads (
          user_id,
          read_at
        )
      `)
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    
    // Process each message to add is_read flag
    const messagesWithReadStatus = data.map(message => {
      // Set is_read true if the current user has a message_reads entry for this message
      const isRead = message.message_reads && message.message_reads.some(r => r.user_id === currentUserId);
      const readAt = isRead
        ? (message.message_reads.find(r => r.user_id === currentUserId)?.read_at || null)
        : null;
      return {
        ...message,
        is_read: isRead,
        read_at: readAt
      };
    });

    return messagesWithReadStatus as unknown as MessageWithReadStatus[];
  } catch (error) {
    handleError(
      error,
      "Failed to load messages",
      ErrorSeverity.ERROR,
      context
    );
    return [];
  }
};

/**
 * Send a new message in a conversation
 */
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  senderType: 'staff' | 'parent',
  content: string,
  organizationId?: string,
  metadata?: Record<string, any>
): Promise<Message> => {
  const context: ErrorContext = {
    operation: "sendMessage",
    additionalInfo: { conversationId, senderId, senderType },
  };

  try {
    // Only include fields that exist in the messages table
    // Required: conversation_id, sender_id, sender_type, content
    // Optional: organization_id, metadata
    const messageToInsert = {
      conversation_id: conversationId,
      sender_id: senderId,
      sender_type: senderType,
      content,
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...(metadata ? { metadata } : {})
    };

    // Insert the message (as an array)
    const { data, error: messageError } = await supabase
      .from('messages')
      .insert([messageToInsert])
      .select()
      .single();

    if (messageError) throw messageError;

    // Call the Edge Function for notifications
    if (data) {
      try {
        await fetch('https://muoihzfomfercsujyyta.functions.supabase.co/message-notifications', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'INSERT',
            record: data
          })
        });
      } catch (err) {
        console.error('Failed to call message notification function:', err);
      }
    }

    // Update the conversation's last_message_at timestamp
    const { error: conversationError } = await supabase
      .from('conversations')
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', conversationId);

    if (conversationError) throw conversationError;

    return data as Message;
  } catch (error) {
    // Log the real error for debugging
    console.error("sendMessage error:", error);
    handleError(
      error,
      "Failed to send message",
      ErrorSeverity.ERROR,
      context
    );
    throw error;
  }
};

/**
 * Create a new conversation
 */
export const createConversation = async (
  organizationId: string,
  familyId: string,
  title: string
): Promise<Conversation> => {
  const context: ErrorContext = {
    operation: "createConversation",
    additionalInfo: { organizationId, familyId, title },
  };

  try {
    const { data, error } = await supabase
      .from('conversations')
      .insert({
        family_id: familyId,
        organization_id: organizationId,
        title,
        last_message_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data as Conversation;
  } catch (error) {
    console.error('Error in operation:', context.operation);
    console.error('Additional context:', context.additionalInfo);
    console.error('Error details:', error);
    throw error;
  }
};

/**
 * Get all conversations for an organization
 */
export const getConversationsForOrganization = async (
  organizationId: string
): Promise<ConversationWithDetails[]> => {
  const context: ErrorContext = {
    operation: "getConversationsForOrganization",
    organizationId,
  };

  try {
    const { data, error } = await tables.conversations()
      .select(
        `
        *,
        family:family_id (
          id,
          name
        )
      `
      )
      .eq("organization_id", organizationId)
      .order("last_message_at", { ascending: false });

    if (error) throw error;
    return (data || []) as unknown as ConversationWithDetails[];
  } catch (error) {
    handleError(
      error,
      "Failed to load conversations",
      ErrorSeverity.ERROR,
      context
    );
    return [];
  }
};

/**
 * Get a specific conversation by ID
 */
export const getConversationById = async (
  conversationId: string
): Promise<Conversation> => {
  const context: ErrorContext = {
    operation: "getConversationById",
    additionalInfo: { conversationId },
  };

  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        family:family_id (
          id,
          name
        )
      `)
      .eq("id", conversationId)
      .single();

    if (error) throw error;
    return data as Conversation;
  } catch (error) {
    console.error('Error in operation:', context.operation);
    console.error('Additional context:', context.additionalInfo);
    console.error('Error details:', error);
    throw error;
  }
};

/**
 * Get all conversations for a specific family
 */
export const getConversationsForFamily = async (
  familyId: string
): Promise<Conversation[]> => {
  const context: ErrorContext = {
    operation: "getConversationsForFamily",
    additionalInfo: { familyId },
  };

  try {
    // Remove the messages:messages join, just fetch conversations and family
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        family:family_id (
          id,
          name
        )
      `)
      .eq("family_id", familyId)
      .order("last_message_at", { ascending: false });

    if (error) throw error;

    // Optionally, fetch the last message for each conversation (if needed for UI)
    const enhancedConversations = await Promise.all(
      data.map(async (conversation: any) => {
        // Fetch the most recent message for this conversation
        const { data: messages, error: msgError } = await supabase
          .from('messages')
          .select('id, content, created_at, sender_type, sender_id')
          .eq('conversation_id', conversation.id)
          .order('created_at', { ascending: false })
          .limit(1);
        const lastMessage = messages && messages.length > 0 ? messages[0] : null;
        return {
          ...conversation,
          lastMessage,
          unreadCount: 0 // Placeholder for unread count
        } as Conversation;
      })
    );

    return enhancedConversations;
  } catch (error) {
    console.error('Error in operation:', context.operation);
    console.error('Additional context:', context.additionalInfo);
    console.error('Error details:', error);
    throw error;
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (
  messageIds: string[],
  userId: string
): Promise<void> => {
  const context: ErrorContext = {
    operation: "markMessagesAsRead",
    additionalInfo: { messageIds, userId },
  };

  if (!messageIds.length) return;

  try {
    const now = new Date().toISOString();
    const readRecords = messageIds.map(messageId => ({
      message_id: messageId,
      user_id: userId,
      read_at: now
    }));

    const { error } = await supabase
      .from('message_reads')
      .upsert(readRecords, { onConflict: 'message_id,user_id' });

    if (error) throw error;
  } catch (error) {
    console.error('Error in operation:', context.operation);
    console.error('Additional context:', context.additionalInfo);
    console.error('Error details:', error);
    throw error;
  }
};
